import { useTranslation } from 'react-i18next';
import { vignetteStaticData } from '../mocks/vignettes';
import { useProducts } from '../hooks/useProducts';
import Header from '../components/feature/Header';
import Footer from '../components/feature/Footer';
import { Link } from 'react-router-dom';

interface CountryPageTemplateProps {
  countryKey: string;
}

export default function CountryPageTemplate({ countryKey }: CountryPageTemplateProps) {
  const { t } = useTranslation();
  const { data: products, loading, error } = useProducts();

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-[15px] text-muted">Loading prices…</div>
        </main>
        <Footer />
      </div>
    );
  }

  const code = countryKey.toUpperCase();
  const country = products?.find(c => c.code === code);
  const staticData = vignetteStaticData[code];

  if (error || !country || !staticData) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-1 flex flex-col items-center justify-center gap-4">
          <p className="text-[15px] text-ink-soft">Could not load pricing data.</p>
          <button
            onClick={() => window.location.reload()}
            className="inline-flex items-center justify-center rounded-lg bg-primary px-5 py-2.5 text-[14px] font-semibold text-white transition hover:bg-primary-hover cursor-pointer"
          >
            Try again
          </button>
        </main>
        <Footer />
      </div>
    );
  }

  const carPrices =
    country.vehicle_types.find(vt => vt.name === 'car')?.prices ??
    country.vehicle_types[0]?.prices ??
    [];

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1">
        <section className="bg-surface border-b border-border">
          <div className="mx-auto max-w-5xl px-5 py-12 md:py-16">
            <div className="flex items-center gap-3 mb-4">
              <img
                src={`https://cdn.jsdelivr.net/gh/lipis/flag-icons@7.2.3/flags/4x3/${staticData.flagCode}.svg`}
                alt={`${country.name} flag`}
                width={40}
                height={30}
                loading="lazy"
                className="rounded-sm"
              />
              <span className="text-[13px] font-semibold uppercase tracking-wider text-muted">E-vignette</span>
            </div>
            <h1 className="text-[28px] md:text-[36px] font-bold tracking-tight">
              {t(`countryPage.${countryKey}.title`)}
            </h1>
            <p className="mt-3 max-w-2xl text-[15px] leading-[1.6] text-ink-soft">
              {t(`countryPage.${countryKey}.description`)}
            </p>
          </div>
        </section>

        <section className="bg-bg">
          <div className="mx-auto max-w-5xl px-5 py-10 md:py-14">
            <h2 className="text-[20px] md:text-[22px] font-semibold tracking-tight">
              {t('countryPage.availableOptions')}
            </h2>
            <div className='mb-[25px] overflow-hidden text-gray-700 text-[15px] leading-6'>
              {t('countryPage.priceNote')}
            </div>
            <div className="overflow-hidden rounded-xl border border-border bg-surface">
              <table className="w-full text-left text-[14px]">
                <thead>
                  <tr className="border-b border-border bg-bg">
                    <th className="px-5 py-3.5 font-semibold text-ink">{t('countryPage.table.period')}</th>
                    <th className="px-5 py-3.5 font-semibold text-ink">{t('countryPage.table.price')}</th>
                    <th className="px-5 py-3.5 font-semibold text-ink text-right">{t('countryPage.table.action')}</th>
                  </tr>
                </thead>
                <tbody>
                  {carPrices.map((price, i) => (
                    <tr key={price.id} className={i < carPrices.length - 1 ? 'border-b border-border' : ''}>
                      <td className="px-5 py-4 text-ink-soft">
                        {t(`countryPage.period.${price.period.toLowerCase().replace(/\s+/g, '')}`, { defaultValue: price.period })}
                      </td>
                      <td className="px-5 py-4 font-semibold text-ink">
                        {parseFloat(price.price).toFixed(2)} {country.currency}
                      </td>
                      <td className="px-5 py-4 text-right">
                        <Link
                          to={`/checkout?country=${country.code.toLowerCase()}&period=${encodeURIComponent(price.period)}&price=${price.price}&productId=${price.id}`}
                          className="inline-flex items-center justify-center rounded-md bg-primary px-5 py-2 text-[13px] font-semibold text-white transition hover:bg-primary-hover cursor-pointer whitespace-nowrap"
                        >
                          {t('countryPage.buy')}
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        <section className="bg-surface border-t border-border">
          <div className="mx-auto max-w-5xl px-5 py-10 md:py-14">
            <h2 className="text-[20px] md:text-[22px] font-semibold tracking-tight mb-4">
              {t('countryPage.importantRules')}
            </h2>
            <ul className="space-y-3">
              {[1, 2, 3].map((n) => (
                <li key={n} className="flex items-start gap-3 text-[14px] text-ink-soft">
                  <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary-soft text-primary">
                    <i className="ri-check-line text-[12px]" />
                  </span>
                  {t(`countryPage.${countryKey}.rule${n}`)}
                </li>
              ))}
            </ul>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
